import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Flag, IconButton} from 'platform/components';
import {Icon} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';

import {join} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {GetTrimLevelsApiResponse, RelativeFilterResponseBody} from '../../../types/api/api';
import {FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';
import {getPresetDisplayName} from '../../Filter/components/SavedFilters/utils/getPresetDisplayName';
import type {IsKnownPresetFeature} from '../../Filter/components/SavedFilters/utils/normalizePresetFeatures';
import {formValuesToRequestValues} from '../../Filter/utils/formValuesToRequestValues';
import {useMarketMetrics} from '../hooks/useMarketMetrics';
import {buildFilterSummary, FilterSummaryHelpers} from '../utils/buildFilterSummary';
import {resolvePresetFormValues, toMetricsRequestBody} from '../utils/buildMetricsRequestBody';
import {PanelGridRow} from './PanelGrid';
import {PresetMetricsCells} from './PresetMetricsCells';

interface PresetMetricsRowProps extends TestIdProps {
  preset: RelativeFilterResponseBody;
  priceReport: PriceReportType;
  /** Form snapshot taken when the panel was expanded — the “what if selected” baseline. */
  baseFormValues: FilterFormData;
  trimLevelsCatalog: GetTrimLevelsApiResponse['trim_levels'] | null;
  isKnownFeature: IsKnownPresetFeature;
  summaryHelpers: FilterSummaryHelpers;
  formatPrice: (price: number | null) => string;
  isActive: boolean;
  isSortable: boolean;
  onSelect: (preset: RelativeFilterResponseBody) => void;
  /** Opens the preset editor for this row. */
  onEdit: (preset: RelativeFilterResponseBody) => void;
}

/**
 * One expanded-panel row: metrics the market would show if this preset were selected.
 * Mounted lazily (only while the panel is expanded), so each row fetches its own
 * statistics on first expand and serves from the RTK Query cache afterwards.
 */
export function PresetMetricsRow(props: PresetMetricsRowProps) {
  const {preset, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature} = props;

  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: preset.id,
    disabled: !props.isSortable,
  });

  const plainBody = useMemo(
    () =>
      formValuesToRequestValues(
        resolvePresetFormValues(
          preset.config,
          priceReport,
          baseFormValues,
          trimLevelsCatalog,
          isKnownFeature
        )
      ),
    [preset.config, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature]
  );

  const queryArg = useMemo(
    () => ({
      reportId: priceReport.id,
      body: toMetricsRequestBody(plainBody, priceReport.sellingPrice.recommended.value ?? null),
    }),
    [priceReport, plainBody]
  );
  const {metrics, isLoading} = useMarketMetrics(queryArg, priceReport);

  const description = useMemo(
    () => join(', ', buildFilterSummary(plainBody, props.summaryHelpers)),
    [plainBody, props.summaryHelpers]
  );

  return (
    <PanelGridRow
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      $isActive={props.isActive}
      $isDragging={isDragging}
      style={{
        transform: CSS.Transform.toString(transform) ?? undefined,
        transition: transition ?? undefined,
      }}
      onClick={() => props.onSelect(preset)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          props.onSelect(preset);
        }
      }}
      data-testid={suffixTestId(`presetRow-${preset.id}`, props)}
    >
      <PresetMetricsCells
        name={getPresetDisplayName(preset)}
        nameBadge={
          preset.default ? (
            <Flag
              label={i18n.t('entity.filterForm.savedFilters.defaultBadge')}
              size="small"
              colorScheme="blue"
              isSubtle
            />
          ) : null
        }
        leadingSlot={
          props.isSortable ? (
            <DragHandle
              {...attributes}
              {...listeners}
              onClick={(event) => event.stopPropagation()}
              data-testid={suffixTestId(`dragHandle-${preset.id}`, props)}
            >
              <Icon value="action/drag_indicator" color="palettes.neutral.60.100" size={4} />
            </DragHandle>
          ) : (
            <DragHandleSpacer />
          )
        }
        description={description}
        metrics={metrics}
        isLoading={isLoading}
        formatPrice={props.formatPrice}
        data-testid={suffixTestId(`presetRow-${preset.id}`, props)}
      />
      <RowActions onClick={(event) => event.stopPropagation()}>
        <IconButton
          icon="action/settings"
          priority="tertiary"
          onClick={() => props.onEdit(preset)}
          aria-label={i18n.t('entity.filterForm.savedFilters.editPreset')}
          data-testid={suffixTestId(`editPreset-${preset.id}`, props)}
        />
      </RowActions>
    </PanelGridRow>
  );
}

const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  /* Stacked layout: the gear sits next to the preset name in the first row */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-row: 1;
    grid-column: 2;
    align-self: start;
  }
`;

/* Drag-to-sort is a desktop affordance — hidden on touch layouts. */
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    display: none;
  }
`;

const DragHandleSpacer = styled.div`
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    display: none;
  }
`;
